import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { getOpenAIStatus, manualQuotaReset } from '../../services/openaiService';
import { testApiKey, getClientStatus } from '../../utils/openaiClient';

const AIStatusDiagnostic = ({ onStatusChange, showHeader = true }) => {
  const [status, setStatus] = useState(null);
  const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const runDiagnostic = async () => {
    setIsRunningDiagnostic(true);
    
    try {
      // Step 1: Check basic status
      const basicStatus = getOpenAIStatus();
      setStatus(basicStatus);
      
      // Step 2: Check client initialization
      const clientStatus = getClientStatus();
      
      // Step 3: Test API key if client is initialized
      let apiTest = null;
      if (clientStatus?.initialized) {
        apiTest = await testApiKey();
      }
      
      const results = {
        timestamp: new Date()?.toISOString(),
        basicStatus,
        clientStatus,
        apiTest,
        overallHealth: determineOverallHealth(basicStatus, clientStatus, apiTest)
      };
      
      setDiagnosticResults(results);
      
      // Notify parent component of status change
      if (onStatusChange) {
        onStatusChange(results?.overallHealth);
      }
      
    } catch (error) {
      console.error('Diagnostic failed:', error);
      setDiagnosticResults({
        timestamp: new Date()?.toISOString(),
        error: error?.message,
        overallHealth: 'error'
      });
    } finally {
      setIsRunningDiagnostic(false);
    }
  };

  const determineOverallHealth = (basicStatus, clientStatus, apiTest) => {
    if (!clientStatus?.initialized) return 'not_configured';
    if (basicStatus?.status === 'quota_exceeded') return 'quota_exceeded';
    if (apiTest && !apiTest?.success) {
      if (apiTest?.error?.type === 'invalid_key') return 'invalid_key';
      if (apiTest?.error?.type === 'quota_exceeded') return 'quota_exceeded';
      if (apiTest?.error?.type === 'insufficient_permissions') return 'insufficient_permissions';
      return 'error';
    }
    if (apiTest && apiTest?.success) return 'healthy';
    return 'unknown';
  };

  useEffect(() => {
    runDiagnostic();
    
    // Auto-refresh every minute
    const interval = setInterval(runDiagnostic, 60000);
    return () => clearInterval(interval);
  }, []);

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'text-success';
      case 'not_configured': return 'text-error';
      case 'invalid_key': return 'text-error';
      case 'quota_exceeded': return 'text-warning';
      case 'insufficient_permissions': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy': return 'CheckCircle';
      case 'not_configured': return 'XCircle';
      case 'invalid_key': return 'AlertCircle';
      case 'quota_exceeded': return 'Clock';
      case 'insufficient_permissions': return 'Shield';
      case 'error': return 'AlertTriangle';
      default: return 'HelpCircle';
    }
  };

  const getHealthMessage = (health) => {
    switch (health) {
      case 'healthy': return 'AI services are working properly';
      case 'not_configured': return 'OpenAI API key needs to be configured';
      case 'invalid_key': return 'OpenAI API key is invalid or expired';
      case 'quota_exceeded': return 'OpenAI usage quota has been exceeded';
      case 'insufficient_permissions': return 'API key lacks necessary permissions';
      case 'error': return 'AI services are experiencing issues';
      default: return 'AI service status unknown';
    }
  };

  const handleManualReset = () => {
    manualQuotaReset();
    runDiagnostic();
  };

  const handleFixApiKey = () => {
    window.open('https://platform.openai.com/account/api-keys', '_blank');
  };

  const handleCheckBilling = () => {
    window.open('https://platform.openai.com/account/billing', '_blank');
  };

  if (!diagnosticResults) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <Icon name="Loader2" size={20} className="animate-spin text-primary" />
          <span className="text-muted-foreground">Running AI diagnostic...</span>
        </div>
      </div>
    );
  }

  const health = diagnosticResults?.overallHealth;

  return (
    <div className="bg-card border border-border rounded-lg">
      {showHeader && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon 
                name={getHealthIcon(health)} 
                size={20} 
                className={getHealthColor(health)}
              />
              <div>
                <h3 className="font-semibold text-foreground">AI Service Status</h3>
                <p className="text-sm text-muted-foreground">{getHealthMessage(health)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <Icon name="Settings" size={14} />
                {showAdvanced ? 'Simple' : 'Advanced'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={runDiagnostic}
                disabled={isRunningDiagnostic}
              >
                <Icon 
                  name={isRunningDiagnostic ? "Loader2" : "RefreshCw"} 
                  size={14} 
                  className={isRunningDiagnostic ? "animate-spin" : ""}
                />
                {isRunningDiagnostic ? 'Testing...' : 'Test Again'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        {/* Status Overview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon 
                name={getHealthIcon(health)} 
                size={18} 
                className={getHealthColor(health)}
              />
              <span className="font-medium text-foreground">Overall Status</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium bg-current/10 ${getHealthColor(health)}`}>
              {health === 'healthy' ? 'Operational' : 
               health === 'not_configured' ? 'Not Configured' :
               health === 'invalid_key' ? 'Invalid Key' :
               health === 'quota_exceeded' ? 'Quota Exceeded' :
               health === 'insufficient_permissions' ? 'Access Denied' : 'Issues Detected'}
            </span>
          </div>

          {/* Action Buttons Based on Status */}
          {health !== 'healthy' && (
            <div className="flex flex-wrap gap-2">
              {['not_configured', 'invalid_key']?.includes(health) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFixApiKey}
                >
                  <Icon name="ExternalLink" size={14} className="mr-2" />
                  Configure API Key
                </Button>
              )}
              
              {health === 'quota_exceeded' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCheckBilling}
                  >
                    <Icon name="CreditCard" size={14} className="mr-2" />
                    Upgrade Plan
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleManualReset}
                  >
                    <Icon name="RotateCcw" size={14} className="mr-2" />
                    Reset Quota
                  </Button>
                </>
              )}
              
              {health === 'insufficient_permissions' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFixApiKey}
                >
                  <Icon name="Shield" size={14} className="mr-2" />
                  Check Permissions
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Advanced Diagnostics */}
        {showAdvanced && (
          <div className="mt-6 space-y-4">
            <h4 className="font-medium text-foreground">Diagnostic Details</h4>
            
            {/* Client Status */}
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                <span className="text-sm text-muted-foreground">Client Initialized</span>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={diagnosticResults?.clientStatus?.initialized ? "Check" : "X"} 
                    size={16} 
                    className={diagnosticResults?.clientStatus?.initialized ? "text-success" : "text-error"}
                  />
                  <span className="text-xs font-medium">
                    {diagnosticResults?.clientStatus?.initialized ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              
              {/* API Key Test */}
              <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                <span className="text-sm text-muted-foreground">API Key Valid</span>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={diagnosticResults?.apiTest?.success ? "Check" : diagnosticResults?.apiTest ? "X" : "Loader2"} 
                    size={16} 
                    className={`${diagnosticResults?.apiTest?.success ? "text-success" : diagnosticResults?.apiTest ?"text-error" : "text-muted-foreground animate-spin"}`}
                  />
                  <span className="text-xs font-medium">
                    {diagnosticResults?.apiTest?.success ? 'Valid' : diagnosticResults?.apiTest ?'Invalid' : 'Testing...'}
                  </span>
                </div>
              </div>
              
              {/* Models Available */}
              {diagnosticResults?.apiTest?.success && (
                <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                  <span className="text-sm text-muted-foreground">Models Available</span>
                  <span className="text-xs font-medium text-foreground">
                    {diagnosticResults?.apiTest?.modelsAvailable || 'Unknown'}
                  </span>
                </div>
              )}
            </div>

            {/* Error Details */}
            {diagnosticResults?.apiTest && !diagnosticResults?.apiTest?.success && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                <h5 className="text-sm font-medium text-error mb-2">API Test Failed</h5>
                <p className="text-xs text-error/80">{diagnosticResults?.apiTest?.error?.message}</p>
                {diagnosticResults?.apiTest?.error?.status && (
                  <p className="text-xs text-error/60 mt-1">
                    HTTP Status: {diagnosticResults?.apiTest?.error?.status}
                  </p>
                )}
              </div>
            )}

            {/* Last Update */}
            <div className="text-xs text-muted-foreground text-center">
              Last diagnostic: {new Date(diagnosticResults.timestamp)?.toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIStatusDiagnostic;